import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Cookie, X } from 'lucide-react';
import {
  getConsent,
  hasConsent,
  setConsent,
  onConsentChange,
} from '@/utils/cookieConsent';

/**
 * Bottom-fixed cookie banner for first-time visitors + a "Manage preferences" modal.
 * The modal can also be opened externally via window.dispatchEvent(new Event('powersure:open-cookie-prefs')).
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Initial mount: show banner if no consent stored yet
  useEffect(() => {
    if (!hasConsent()) setBannerOpen(true);
    const existing = getConsent();
    if (existing) {
      setAnalytics(Boolean(existing.analytics));
      setMarketing(Boolean(existing.marketing));
    }
  }, []);

  // External "open preferences" trigger (footer link, etc.)
  useEffect(() => {
    const open = () => {
      const existing = getConsent();
      if (existing) {
        setAnalytics(Boolean(existing.analytics));
        setMarketing(Boolean(existing.marketing));
      }
      setModalOpen(true);
    };
    window.addEventListener('powersure:open-cookie-prefs', open);
    return () => window.removeEventListener('powersure:open-cookie-prefs', open);
  }, []);

  // Listen for consent changes (e.g. from another tab) so toggles stay in sync
  useEffect(() => {
    const off = onConsentChange((c) => {
      setAnalytics(Boolean(c.analytics));
      setMarketing(Boolean(c.marketing));
    });
    return off;
  }, []);

  const acceptAll = useCallback(() => {
    setConsent({ analytics: true, marketing: true });
    setBannerOpen(false);
    setModalOpen(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    setConsent({ analytics: false, marketing: false });
    setBannerOpen(false);
    setModalOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    setConsent({ analytics, marketing });
    setBannerOpen(false);
    setModalOpen(false);
  }, [analytics, marketing]);

  const openPrefs = useCallback(() => setModalOpen(true), []);

  if (!bannerOpen && !modalOpen) return null;

  return (
    <>
      {bannerOpen && !modalOpen && (
        <div
          className="fixed inset-x-2 bottom-2 sm:inset-x-4 sm:bottom-4 z-[60] rounded-xl shadow-2xl border border-border bg-background/95 backdrop-blur-md p-4 sm:p-5 max-w-3xl sm:left-1/2 sm:-translate-x-1/2"
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-banner-heading"
          data-testid="cookie-consent-banner"
        >
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex w-9 h-9 rounded-lg bg-primary/10 items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="cookie-banner-heading"
                className="text-base font-semibold mb-1 text-foreground"
                style={{ fontFamily: 'Outfit' }}
              >
                {t('cookies.banner.heading')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('cookies.banner.body')}{' '}
                <Link
                  to="/privacy"
                  className="underline hover:text-foreground"
                  data-testid="cookie-banner-privacy-link"
                >
                  {t('cookies.banner.readMore')}
                </Link>
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 font-medium"
                  data-testid="cookie-accept-all"
                >
                  {t('cookies.actions.acceptAll')}
                </button>
                <button
                  type="button"
                  onClick={rejectNonEssential}
                  className="px-4 py-2 text-sm rounded-md bg-background text-foreground hover:bg-muted/40 border-2 border-primary font-medium"
                  data-testid="cookie-reject-non-essential"
                >
                  {t('cookies.actions.rejectNonEssential')}
                </button>
                <button
                  type="button"
                  onClick={openPrefs}
                  className="px-4 py-2 text-sm rounded-md text-foreground hover:bg-muted/60 underline-offset-2 hover:underline"
                  data-testid="cookie-manage-prefs"
                >
                  {t('cookies.actions.manage')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40 p-2 sm:p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-prefs-heading"
          data-testid="cookie-prefs-modal"
        >
          <div className="w-full max-w-lg bg-background rounded-xl shadow-2xl border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between p-5 border-b border-border">
              <div>
                <h2
                  id="cookie-prefs-heading"
                  className="text-lg font-semibold text-foreground"
                  style={{ fontFamily: 'Outfit' }}
                >
                  {t('cookies.prefs.heading')}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">{t('cookies.prefs.subhead')}</p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={t('cookies.actions.close')}
                data-testid="cookie-prefs-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <PrefRow
                title={t('cookies.categories.necessary.title')}
                desc={t('cookies.categories.necessary.desc')}
                value={true}
                disabled
                testid="cookie-toggle-necessary"
                onChange={() => {}}
              />
              <PrefRow
                title={t('cookies.categories.analytics.title')}
                desc={t('cookies.categories.analytics.desc')}
                value={analytics}
                onChange={setAnalytics}
                testid="cookie-toggle-analytics"
              />
              <PrefRow
                title={t('cookies.categories.marketing.title')}
                desc={t('cookies.categories.marketing.desc')}
                value={marketing}
                onChange={setMarketing}
                testid="cookie-toggle-marketing"
              />
            </div>

            <div className="p-5 border-t border-border flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={rejectNonEssential}
                className="px-4 py-2 text-sm rounded-md bg-muted text-foreground hover:bg-muted/80 border border-border"
                data-testid="cookie-prefs-reject"
              >
                {t('cookies.actions.rejectNonEssential')}
              </button>
              <button
                type="button"
                onClick={savePrefs}
                className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 font-medium"
                data-testid="cookie-prefs-save"
              >
                {t('cookies.actions.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PrefRow({ title, desc, value, onChange, disabled, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
      </div>
      <label className={`relative inline-flex items-center shrink-0 mt-1 ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          className="sr-only peer"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          data-testid={testid}
        />
        <span className="w-11 h-6 bg-muted rounded-full peer-checked:bg-primary peer-disabled:bg-primary/60 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:w-5 after:h-5 after:transition-transform peer-checked:after:translate-x-5" />
      </label>
    </div>
  );
}
